import React, { memo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Code2, Layers, Menu, Sparkles } from 'lucide-react';
import { NavLink } from '@/types/nav-link.model';
import { getHeaderBreadcrumb } from './topNavBarUtils';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useWorkspaceSidebar } from './WorkspaceSidebarContext';
import {
  COMPONENTS_PAGE_INSET_LEFT,
  COMPONENTS_PAGE_INSET_RIGHT,
} from '@/components/smartai/ComponentsList/components-page-layout';
import {
  WORKSPACE_HEADER_HEIGHT,
  WORKSPACE_SIDEBAR_WIDTH,
} from './workspace-layout';

interface TopNavBarProps {
  navLinks: NavLink[];
  variant?: 'default' | 'inventory';
  showMobileMenu?: boolean;
  /** When true, header is in-flow inside WorkspaceShell (not fixed MainLayout chrome). */
  embedded?: boolean;
}

const utilityLinkClass =
  'inline-flex h-full cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-[0.8125rem] font-medium leading-none text-[var(--inv-text-secondary)] transition-colors hover:text-[var(--inv-text)] focus-visible:text-[var(--inv-text)] focus-visible:outline-none';

const TopNavBar: React.FC<TopNavBarProps> = ({
  variant = 'default',
  showMobileMenu = false,
  embedded = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const workspaceSidebar = useWorkspaceSidebar();

  const breadcrumb = getHeaderBreadcrumb(location.pathname);
  
  const handleBreadcrumbNavigate = () => {
    if (breadcrumb.homeRoute) {
      navigate(`/${breadcrumb.homeRoute}`);
    }
  };

  const isInventory = variant === 'inventory';

  return (
    <header
      className={cn(
        'top-nav-container z-[110] flex w-full shrink-0 items-center border-0 border-b border-solid bg-[var(--inv-bg)]',
        isInventory
          ? 'h-[var(--workspace-header-height)] border-[var(--inv-border)]'
          : 'h-14 border-[var(--inv-border)]',
        // Embedded = in-flow shell header (flush to viewport via parent).
        // Legacy default = fixed chrome for non-inventory routes.
        embedded
          ? 'sticky top-0 z-[110]'
          : cn(
              'fixed top-0',
              isInventory &&
                'right-0 w-auto max-[900px]:left-0 max-[900px]:w-full min-[901px]:left-[var(--workspace-sidebar-width)]',
            ),
      )}
      style={
        isInventory && !embedded
          ? ({
              '--workspace-sidebar-width': WORKSPACE_SIDEBAR_WIDTH,
              '--workspace-header-height': WORKSPACE_HEADER_HEIGHT,
            } as React.CSSProperties)
          : isInventory
            ? ({
                '--workspace-header-height': WORKSPACE_HEADER_HEIGHT,
              } as React.CSSProperties)
            : undefined
      }>
      <nav
        className={cn(
          'flex h-full min-w-0 flex-1 items-center gap-1.5 overflow-hidden text-[0.8125rem] leading-none',
          isInventory ? COMPONENTS_PAGE_INSET_LEFT : 'pl-5',
        )}
        aria-label="Breadcrumb">
        {showMobileMenu && workspaceSidebar ? (
          <Button
            type="button"
            variant="ghost"
            className="mr-1 inline-flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-md border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] p-0 text-[var(--inv-text)] transition-colors hover:bg-[var(--inv-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)] max-[900px]:inline-flex min-[901px]:hidden"
            aria-label={workspaceSidebar.mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={workspaceSidebar.mobileOpen}
            onClick={workspaceSidebar.toggleMobile}>
            <Menu className="h-4 w-4" aria-hidden />
          </Button>
        ) : null}
        <div className="flex min-w-0 items-center gap-1.5 overflow-hidden">
          <span className="shrink-0 font-normal text-[var(--inv-text-muted)] max-[640px]:hidden">
            {breadcrumb.root}
          </span>
          <span
            className="shrink-0 text-[var(--inv-text-muted)] max-[640px]:hidden"
            aria-hidden>
            /
          </span>
          {breadcrumb.homeRoute ? (
            <Button
              type="button"
              variant="ghost"
              className="h-auto min-w-0 truncate cursor-pointer border-0 bg-transparent p-0 text-[0.8125rem] font-semibold leading-none text-[var(--inv-text)] hover:bg-transparent hover:text-[var(--inv-accent)] focus-visible:text-[var(--inv-accent)] focus-visible:outline-none"
              onClick={handleBreadcrumbNavigate}>
              {breadcrumb.current}
            </Button>
          ) : (
            <span className="min-w-0 truncate text-[0.8125rem] font-semibold leading-none text-[var(--inv-text)]">
              {breadcrumb.current}
            </span>
          )}
        </div>
      </nav>

      <div
        className={cn(
          'inline-flex h-full shrink-0 items-center',
          isInventory
            ? cn('gap-5 max-md:gap-3', COMPONENTS_PAGE_INSET_RIGHT)
            : 'gap-3 pr-5',
        )}>
        <div
          className="inline-flex h-full items-center gap-5 max-[900px]:gap-3"
          data-topnav-actions>
          {/* <Button
            type="button"
            variant="ghost"
            className={cn(utilityLinkClass, 'hover:bg-transparent')}>
            <Code2 className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
            <span className="hidden min-[961px]:inline">CodeKit</span>
          </Button> */}
          <Button
            type="button"
            variant="ghost"
            className={cn(utilityLinkClass, 'hover:bg-transparent')}
            onClick={() => navigate('/sia/coveragekit')}>
            <Layers className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
            <span className="hidden min-[961px]:inline">CoverageKit</span>
          </Button>
          {/* <Button
            type="button"
            variant="ghost"
            className={cn(utilityLinkClass, 'hover:bg-transparent')}
            onClick={() => navigate('/sia/assistant')}>
            <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
            <span className="hidden min-[961px]:inline">Assistant</span>
          </Button> */}
        </div>
      </div>
    </header>
  );
};

export default memo(TopNavBar);
